import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { createAction, props, on, createSelector } from '@ngrx/store';
import { EntityStoreFactory, IGenericEntityState } from '@pit/core-store';
import { IDtoDevice } from '@pit/frontend-model';
import { EntityDataServiceAccessorService } from '@pit/core-data';
import { IDtoDeviceEntityDataService } from '../../module.data/services/interfaces/dto.device.entity.data.service.interface';

export class DtoDeviceStoreFactory extends EntityStoreFactory<IDtoDevice> {

    private static _instance: DtoDeviceStoreFactory = null;

    public static get instance(): DtoDeviceStoreFactory {
        if (this._instance === null) {
            this._instance = new DtoDeviceStoreFactory();
        }

        return this._instance;
    }

    constructor() {
        super('DtoDevice', 'dto.device');
    }

    protected override doCreateActions(): void {
        super.doCreateActions();

        this.entityActions.add('get.by.depot', createAction(
            'Get Devices By Depot',
            props<{ mandator: string, numberDepot: string }>()
        ));
        this.entityActions.add('get.by.depot.success', createAction(
            'Get Devices By Depot Success',
            props<{ items: IDtoDevice[] }>()
        ));
        this.entityActions.add('get.by.depot.failure', createAction(
            'Get Devices By Depot Failure',
            props<{ error: any }>()
        ));
    }

    protected override doCreateEffects(): void {
        super.doCreateEffects();

        this.entityEffects.add('get.by.depot', {
            effect: (actions$: Actions, dataServiceAccessor: EntityDataServiceAccessorService) => {
                return createEffect(() =>
                    actions$.pipe(
                        ofType(this.getAction('get.by.depot')),
                        map(action => action),
                        switchMap(action =>
                            (dataServiceAccessor.getEntityDataService<IDtoDevice>(this.dataAccessName) as IDtoDeviceEntityDataService)
                                .getDevicesForMandatorByDepotNumber$(action.mandator, action.numberDepot).pipe(
                                    map(items => this.getAction('get.by.depot.success')({ items: items })),
                                    catchError(error => of(this.getAction('get.by.depot.failure')({ error: error })))
                                )
                        )
                    )
                );
            }
        });
    }

    protected override doCreateSelectors() {
        super.doCreateSelectors();

        this.entitySelectors.add('get.by.deviceid', (deviceId: string) => createSelector(
            this.getSelector('get.all.entities'),
            (items: IDtoDevice[]) => items.find(item => (item.deviceId === deviceId))
        ));

        this.entitySelectors.add('get.devices.sorted.by.account', createSelector(
            this.getSelector('get.all.entities'),
            // Copy before sorting (the selector result is shared/memoized) and guard
            // against devices without an account: some depot devices have a null
            // account, and calling localeCompare on null throws, which errors the
            // observable and leaves any bound combo box permanently empty.
            (items: IDtoDevice[]) => [...items].sort((x, y) => (x.account ?? '').localeCompare(y.account ?? ''))
        ));

        this.entitySelectors.add('get.devices.sorted.by.modifiedat', createSelector(
            this.getSelector('get.all.entities'),
            (items: IDtoDevice[]) => items.sort((x, y) => (y.modifiedAt.getTime() - x.modifiedAt.getTime()))
        ));
    }

    protected override doGetReducers<S extends IGenericEntityState<IDtoDevice>>(): any {
        const result: any = super.doGetReducers();

        result.push(
            on(
                this.getAction('get.by.depot'),
                (state: any, { showLoadingIndicator }) => {
                    return {
                        ...state,
                        loading: ((showLoadingIndicator === true) ? true : state.loading)
                    };
                }
            )
        );
        result.push(
            on(
                this.getAction('get.by.depot.failure'),
                (state: any) => {
                    return {
                        ...state,
                        loading: ((state.loading === true) ? false : state.loading)
                    };
                }
            )
        );
        result.push(
            on(this.getAction('get.by.depot.success'), (state: any, { items }) => {
                const res = this.entityAdapter.setAll(items, {
                    ...state,
                    loading: false,
                    selectedItem: null
                });
                return res;
            })
        );

        return result;
    }

}
